import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import ServiceCard from '../ui/ServiceCard';
import { services } from '../../data/services';

const ServicesSection = () => {
  const maxDisplayedServices = 4;
  const displayedServices = services.slice(0, maxDisplayedServices);
  const hasMoreServices = services.length > maxDisplayedServices;

  // This function now serves as the single point of navigation
  const handleExploreSolutions = () => {
    window.location.href = '/services';
  };

  const targetRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: targetRef,
    offset: ["start start", "end start"],
  });

  const opacity = useTransform(scrollYProgress, [0, 0.25], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.25], [1, 0.8]);

  return (
    <section id="services" ref={targetRef} className="relative bg-primary-bg">
      <div className="sticky top-0 h-screen flex items-center justify-center">
        <motion.div
          style={{ opacity, scale }}
          className="text-center px-6"
        >
          <h2 className="text-4xl lg:text-6xl font-display font-bold text-white mb-8">
            Our Services
          </h2>
          <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed mb-12">
          We don’t just model data; we model the grid. Built on a proprietary data pipeline, MarvinAI ingests regional congestion metrics, high-resolution real-time weather data, and continuous portfolio performance to feed our predictive algorithms. This allows us to deliver superior forecasting accuracy for HUPX traders and asset managers who require a deeper, local understanding of market drivers beyond simple historical averages.          </p>
          
          {/* Service Keywords */}
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-wrap justify-center gap-x-8 gap-y-4 text-base lg:text-lg">
              <span className="text-primary-cyan font-medium">Day-Ahead</span>
              <span className="text-white/60">•</span>
              <span className="text-primary-cyan font-medium"> Intraday (IDA1, 2, 3, C) </span>
              <span className="text-white/60">•</span>
              <span className="text-primary-cyan font-medium"> Balancing Energy Price & Volume Forecasts  </span>
              <span className="text-primary-cyan font-medium"> Renewable Energy Generation and Co-located Asset Optimization </span>
              <span className="text-primary-cyan font-medium w-full">Market Analytics, insights and data services</span>
            </div>
          </div>
        </motion.div>
      </div>

      <div className="relative z-10 -mt-[100vh]">
        <div className="h-screen" />

        <div className="container mx-auto px-6 pb-20 lg:pb-32 bg-primary-bg">
          <div className="space-y-16 lg:space-y-20">
            {displayedServices.map((service, index) => {
              const isEven = index % 2 === 0;
              return (
                <motion.div
                  key={service.id}
                  className={`relative ${isEven ? 'lg:pr-12' : 'lg:pl-12'}`}
                  initial={{ opacity: 0, x: isEven ? -60 : 60, y: 40, rotate: isEven ? -1 : 1 }}
                  whileInView={{ opacity: 1, x: 0, y: 0, rotate: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{
                    duration: 0.7,
                    delay: index * 0.12,
                    ease: [0.4, 0, 0.2, 1]
                  }}
                >
                  <ServiceCard 
                    service={service}
                  />
                </motion.div>
              );
            })}
          </div>

          {/* UPDATED: Central "Learn More" / "Explore" Button */}
          {hasMoreServices && (
            <motion.div
              className="text-center mt-16 lg:mt-24"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <motion.button
                onClick={handleExploreSolutions}
                className="group inline-flex items-center space-x-4 bg-glass-light backdrop-blur-md border border-white/10 px-8 py-4 rounded-2xl text-white hover:bg-glass-medium transition-all duration-300"
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.98 }}
              >
                {/* Text changed to be more encompassing */}
                <span className="text-lg font-medium">Explore All Our Solutions</span>
                <ArrowRight 
                  size={20} 
                  className="group-hover:translate-x-1 transition-transform duration-300" 
                />
              </motion.button>
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;